<template>
  <div
    class="border-b border-gray-200 last:border-b-0"
  >
    <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3 ml-4">
          <h4
            class="text-lg font-semibold text-gray-900"
            :class="{ 'text-red-600': !trade.forecastDuration }"
          >
            {{ trade.name }}
          </h4>

          <div
            v-if="!trade.forecastDuration"
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3 mr-1">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
</svg>

            {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.missingForecastDates') }}
          </div>
        </div>

        <div
          v-if="trade.forecastDuration"
          class="flex items-center space-x-4 text-sm"
        >
          <div class="flex items-center space-x-2">
            <span class="text-gray-500">{{ $t('HeadContract.CashflowForecasting.TradeForecastRow.duration') }}:</span>

            <span class="font-medium text-gray-900">{{ trade.forecastDuration }} {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.months', trade.forecastDuration) }}</span>
          </div>

          <div class="flex items-center space-x-2">
            <span class="text-gray-500">{{ $t('HeadContract.CashflowForecasting.TradeForecastRow.allocated') }}:</span>

            <span
              :class="[totalDifference !== 0 ? 'text-red-600' : 'text-green-600']"
              class="font-medium"
            >
              {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.allocatedTotal', { allocated: $tcc(totalAmount), total: $tcc(trade.totalBudget) }) }}
              <span v-if="totalDifference !== 0">({{ (totalDifference > 0 ? "+" : "") + $tcc(totalDifference) }})</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="flex p-2 ml-8 space-x-2">
      <div class="flex flex-col bg-gray-50 rounded-lg p-4 border-gray-200 border">
        <h5 class="text-sm font-medium text-gray-900 mb-4">
          {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.forecastDates') }}
        </h5>

        <form
          class="flex gap-4 h-full"
          :class="[trade.forecastDuration ? 'flex-col' : 'flex-row items-end']"
          @submit.prevent="updateTrade"
        >
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.label.startDate') }}
            </label>

            <AppInput
              v-model="forecastDatesForm.form.forecast_start_date"
              name="forecast_start_date"
              type="date"
              :error="forecastDatesForm.getErrors('forecast_start_date')"
              class="form-input w-full"
              :disabled="!projectsStore.project?.unarchived"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.label.endDate') }}
            </label>

            <AppInput
              v-model="forecastDatesForm.form.forecast_end_date"
              name="forecast_end_date"
              type="date"
              :error="forecastDatesForm.getErrors('forecast_end_date')"
              class="form-input w-full"
              :disabled="!projectsStore.project?.unarchived"
            />
          </div>

          <button
            v-if="projectsStore.project?.unarchived"
            class="btn btn--secondary-blue w-full flex items-center justify-center"
            type="submit"
            :disabled="updateTradeAction.is(States.LOADING)"
          >
            <AppSpinner
              v-if="updateTradeAction.is(States.LOADING)"
              class="mr-1"
              spinner-class="w-4 h-4"
            />

            {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.button.saveDates') }}
          </button>
        </form>
      </div>

      <div
        v-if="trade.forecastDuration"
        class="overflow-x-auto w-full bg-gray-50 rounded-lg p-4 border border-gray-200"
      >
        <div class="sticky left-0 flex flex-col">
          <div class="flex items-center justify-between mb-4">
            <div class="flex space-x-4 mb-auto items-center">
              <h5 class="text-sm font-medium text-gray-900">
                {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.monthlyAllocation') }}
              </h5>

              <div
                v-if="trade.baseline_start_date && trade.baseline_end_date"
                class="text-xs text-gray-500"
              >
                {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.baseline') }}: {{ DateTime.fromFormat(trade.baseline_start_date, 'yyyy-MM-dd').toFormat('MMM yyyy') }} - {{ DateTime.fromFormat(trade.baseline_end_date, 'yyyy-MM-dd').toFormat('MMM yyyy') }}
              </div>
            </div>

            <div class="flex items-center space-x-2">
              <template v-if="projectsStore.project?.unarchived">
                <button
                  type="button"
                  class="btn btn--tertiary-blue text-sm"
                  @click="resetForecastLinear"
                >
                  {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.button.resetLinear') }}
                </button>

                <button
                  v-if="trade.forecast?.length > 0"
                  type="button"
                  class="btn btn--tertiary-blue text-sm"
                  @click="revertForecast"
                >
                  {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.button.revert') }}
                </button>

                <button
                  type="button"
                  class="btn btn--secondary-blue text-sm disabled:opacity-50 flex items-center"
                  :disabled="updateForecastAction.is(States.LOADING)"
                  @click="updateForecast"
                >
                  <AppSpinner
                    v-if="updateForecastAction.is(States.LOADING)"
                    class="mr-1"
                    spinner-class="w-4 h-4"
                  />
                  {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.button.save') }}
                  <span
                    v-if="forecastForm.isDirty.value"
                    class="ml-1 text-xs"
                  >
                    {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.button.unsaved') }}
                  </span>
                </button>
              </template>
            </div>
          </div>

          <div
            v-if="forecastForm.errors['forecast']?.[0] ?? false"
            class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md"
          >
            <div class="flex">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-red-400 mr-2">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
</svg>

              <span class="text-sm text-red-800 first-letter:capitalize">
                {{ forecastForm.errors['forecast'][0] ?? '' }}
              </span>
            </div>
          </div>

          <div
            v-if="!forecastsSet"
            class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md"
          >
            <div class="flex">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-yellow-400 mr-2">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
</svg>

              <span class="text-sm text-yellow-800">
                {{ Math.abs(forecastForm.form.forecast.length - props.trade.forecastDuration) }} {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.forecastsMissing') }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex py-5 pr-5">
          <div
            v-if="trade.forecastDuration && updateTradeAction.isNot(States.LOADING)"
            class="flex pr-5"
          >
            <table>
              <thead>
                <tr :data-month-row="trade.id">
                  <th class="sticky -left-4 bg-gray-50 pr-2" />
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td class="sticky -left-4 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider py-2 px-3">
                    {{ $t('HeadContract.CashflowForecasting.TradeForecastRow.baseline') }}
                  </td>

                  <template v-if="trade.combinedStart < trade.baselineStart">
                    <td
                      v-for="month in combinedStartDiff"
                      :key="month"
                      class="text-center"
                    >
                      -
                    </td>
                  </template>

                  <td
                    v-for="(baselineAmount, i) in trade.baseline_forecast"
                    :key="i"
                    class="text-center p-1"
                  >
                    {{ $tcc(baselineAmount) }}/{{ ((baselineAmount / trade.totalBudget) * 100).toFixed(2) }}%
                  </td>

                  <template v-if="trade.combinedEnd > trade.baselineEnd">
                    <td
                      v-for="month in combinedEndDiff"
                      :key="month"
                      class="text-center"
                    >
                      -
                    </td>
                  </template>
                </tr>

                <tr
                  :data-percent-row="trade.id"
                >
                  <td class="sticky -left-4 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider py-2 px-3 text-center">
                    %
                  </td>
                </tr>

                <tr
                  :data-amount-row="trade.id"
                >
                  <td class="sticky -left-4 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider py-2 px-3 text-center">
                    {{ $t('currency.symbol') }}
                  </td>
                </tr>
              </tbody>
            </table>

            <template
              v-for="month in trade.combinedDuration"
              :key="month"
            >
              <ForecastInput
                v-if="(month > combinedStartDiff || forecastStartsEarlier) && (month <= (trade.combinedDuration - combinedEndDiff) || forecastEndsLater)"
                v-model:percent="forecastForm.form.forecast_percents[month - (forecastStartsEarlier ? 0 : combinedStartDiff) - 1]"
                v-model:amount="forecastForm.form.forecast[month - (forecastStartsEarlier ? 0 : combinedStartDiff) - 1]"
                :trade="trade"
                :month-index="month"
                :actual-filled="trade.actuals_filled[month - (forecastStartsEarlier ? 0 : combinedStartDiff) - 1]"
                :disabled="!projectsStore.project?.unarchived"
                :error="forecastForm.hasErrors(`forecast.${month - (forecastStartsEarlier ? 0 : combinedStartDiff) - 1}`)"
                @save-percent="proRataSplitPercents(month - (forecastStartsEarlier ? 0 : combinedStartDiff) - 1, $event.oldAmount, $event.newAmount)"
              />

              <DisabledInputs
                v-else
                :trade="trade"
                :month-index="month"
              />
            </template>
          </div>

          <div
            v-else-if="updateTradeAction.is(States.LOADING)"
            class="flex justify-center items-center w-full my-20"
          >
            <AppSpinner />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { cumulativeSum, dateForDateInput, falsey, roundDecimals } from '@/helpers';
import Trade from '@/models/Trade';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import DisabledInputs from './DisabledInputs.vue';
import ForecastInput from './ForecastInput.vue';

const props = defineProps<{
  trade: Trade;
}>();

const emits = defineEmits<{
  (e: 'forecastUpdated', data: { newForecasts: number[] });
  (e: 'forecastSaved');
}>();

const tradesStore = useTradesStore();
const projectsStore = useProjectsStore();

const forecastForm = useFormData({
  forecast: [] as (number[]),
  forecast_percents: [] as (number[]),
});

const totalAmount = computed(() => {
  return forecastForm.form.forecast.reduce((total, current) => {
    return total + (current || 0);
  }, 0);
});

const totalDifference = computed(() => {
  return roundDecimals(totalAmount.value - props.trade.totalBudget);
});

const forecastsSet = computed(() => {
  return forecastForm.form.forecast.length === props.trade.forecastDuration;
});

watch(
  [() => props.trade.forecast_start_date, () => props.trade.forecast_end_date, () => props.trade.forecast],
  (newValue) => {
    if(newValue) {
      // get months between forecast start/end
      // create array based on that, rather than just copying what's saved

      forecastForm.form.forecast = [];

      if(props.trade.forecast?.length > 0) {
        for(let i = 0;i < props.trade.forecastDuration;i++) {
          forecastForm.form.forecast[i] = props.trade.forecast[i] ?? 0;
          // console.log(forecastForm.form.forecast[i]);
        }
      }

      forecastForm.form.forecast_percents = forecastForm.form.forecast.map((forecastAmount) => {
        return roundDecimals(forecastAmount / props.trade.totalBudget * 100);
      });

      nextTick(() => {
        forecastForm.resetDirtyStatus();
      });
    }
  },
  { immediate: true },
);

const proRataSplitPercents = (index: number, oldAmount: number, newAmount: number) => {
  // console.log('pro rata split');

  // get all months after

  const monthsAfter = props.trade.forecastDuration - (index + 1);

  if(monthsAfter > 0) {
    // get the difference between old and new value
    const amountDiff = oldAmount - newAmount;

    // divide difference by months after

    const amountDiffPerMonth = roundDecimals(amountDiff / monthsAfter);

    // add to each month
    // console.log(monthsAfter, percentDiffPerMonth, oldPercent, newPercent);

    let totalAmount = 0;

    for(let i = index + 1;i <= monthsAfter + index;i++) {
      // NOTE: Math.max will return NaN if it's passed in, so we convert it to 0
      forecastForm.form.forecast[i] = Math.max(
        roundDecimals(
          forecastForm.form.forecast[i] + amountDiffPerMonth,
        ) || 0,
        0,
      );
    }

    forecastForm.form.forecast.forEach((amount) => {
      totalAmount += amount || 0;
    });

    let totalDifference = roundDecimals(props.trade.totalBudget - totalAmount);
    const additionalPerMonth = roundDecimals(totalDifference / monthsAfter);

    if(additionalPerMonth > 0.01) {
      for(let i = index + 1;i <= monthsAfter + index;i++) {
        // NOTE: Math.max will return NaN if it's passed in, so we convert it to 0
        forecastForm.form.forecast[i] = Math.max(
          roundDecimals(
            forecastForm.form.forecast[i] + additionalPerMonth,
          ) || 0,
          0,
        );

        totalAmount += additionalPerMonth;
      }
    }

    totalDifference = roundDecimals(props.trade.totalBudget - totalAmount);

    if(totalDifference !== 0) {
      forecastForm.form.forecast[forecastForm.form.forecast.length - 1] = Math.max(
        forecastForm.form.forecast[forecastForm.form.forecast.length - 1] +
            roundDecimals(totalDifference) || 0,
        0,
      );
    }
  }

  emits('forecastUpdated', { newForecasts: forecastForm.form.forecast });
};

const updateForecastAction = useStoreApiAction(tradesStore.updateForecast);

const updateForecast = () => {
  forecastForm.resetErrors();

  console.log(cumulativeSum(forecastForm.form.forecast), forecastForm.form.forecast);

  updateForecastAction.request(props.trade.id, forecastForm.form).then((data) => {
    // console.log(data);

    Swal.fire({
      icon: 'success',
      text: 'Forecast forecast saved.',
    });

    emits('forecastSaved');
  }).catch((error) => {
    if(error.status === 422) {
      Swal.fire({
        icon: 'error',
        text: error.data.message,
      });

      forecastForm.setErrors(error.data);
    }
  });
};

const resetForecastLinear = () => {
  // total budget - actuals filled

  const remainingBudget = props.trade.totalBudget - props.trade.totalForecastActuals;
  let monthsWithoutActuals = 0;

  for(let i = 0;i < props.trade.forecastDuration;i++) {
    if(!props.trade.actuals_filled[i]) {
      monthsWithoutActuals += 1;
    }
  }

  if(monthsWithoutActuals > 0 && remainingBudget > 0) {
    const amountPerMonth = remainingBudget / monthsWithoutActuals;
    let totalAmount = 0;

    for(let i = 0;i < props.trade.forecastDuration;i++) {
      if(!props.trade.actuals_filled[i]) {
        forecastForm.form.forecast[i] = roundDecimals(amountPerMonth);

        totalAmount += forecastForm.form.forecast[i];
      }
    }

    if(forecastForm.form.forecast.length !== props.trade.forecastDuration) {
      forecastForm.form.forecast = forecastForm.form.forecast.slice(
        0,
        props.trade.forecastDuration - 1,
      );
    }

    const totalDifference = roundDecimals(remainingBudget - totalAmount);

    if(totalDifference !== 0) {
      const latestForecastWithNoActual = props.trade.actuals_filled.findIndex((actual) => actual === false);

      if(latestForecastWithNoActual !== -1) {
        forecastForm.form.forecast[latestForecastWithNoActual] = roundDecimals(
          forecastForm.form.forecast[latestForecastWithNoActual] + totalDifference,
        );
      }
    }

    emits('forecastUpdated', { newForecasts: forecastForm.form.forecast });
  }
};

const revertForecast = () => {
  forecastForm.form.forecast = [];

  if(props.trade.forecast?.length > 0) {
    for(let i = 0;i < props.trade.forecastDuration;i++) {
      forecastForm.form.forecast[i] = props.trade.forecast[i] ?? 0;
      // console.log(forecastForm.form.forecast[i]);
    }
  }

  forecastForm.form.forecast_percents = forecastForm.form.forecast.map((forecastAmount) => {
    return roundDecimals(forecastAmount / props.trade.totalBudget * 100);
  });

  nextTick(() => {
    forecastForm.resetDirtyStatus();
  });

  emits('forecastUpdated', { newForecasts: forecastForm.form.forecast });
};

const forecastDatesForm = useFormData({
  forecast_start_date: '',
  forecast_end_date: '',
});

watch(() => props.trade, (newValue) => {
  if(newValue) {
    forecastDatesForm.setData(newValue);
  }
}, { immediate: true });

watch(
  () => [props.trade, projectsStore.project?.contract_end_date, projectsStore.project?.contract_start_date],
  (newValue) => {
    if(newValue[0]) {
      forecastDatesForm.setData((newValue as [Trade, string, string])[0]);

      if(falsey(forecastDatesForm.form.forecast_start_date)) {
        forecastDatesForm.form.forecast_start_date = dateForDateInput(projectsStore.project?.contract_start_date);
      }

      if(falsey(forecastDatesForm.form.forecast_end_date)) {
        forecastDatesForm.form.forecast_end_date = dateForDateInput(projectsStore.project?.contract_end_date);
      }
    }
  },
  { immediate: true },
);

const updateTradeAction = useStoreApiAction(tradesStore.updateTrade);

const updateTrade = () => {
  forecastDatesForm.resetErrors();

  updateTradeAction.request(props.trade.id, forecastDatesForm.form).then((data) => {
    // console.log(data);

    if(!props.trade.forecast) {
      // Set initial linear forecast after forecast dates established
      resetForecastLinear();
    }
  }).catch((error) => {
    forecastDatesForm.setErrors(error.data);
  });
};

const combinedStartDiff = computed(() => {
  return Math.ceil(Math.abs(props.trade.combinedStart.diff(props.trade.forecastStart, ['months']).months)) ||
    Math.ceil(Math.abs(props.trade.combinedStart.diff(props.trade.baselineStart, ['months']).months));
});

const forecastStartsEarlier = computed(() => {
  return props.trade.forecastStart < props.trade.baselineStart;
});

const forecastEndsLater = computed(() => {
  return props.trade.forecastEnd > props.trade.baselineEnd;
});

const combinedEndDiff = computed(() => {
  let diff = 0;

  if(props.trade.combinedEnd.equals(props.trade.forecastEnd)) {
    diff = Math.ceil(Math.abs(props.trade.combinedEnd.diff(props.trade.baselineEnd, ['months']).months)) -
      (props.trade.baselineEnd.day < props.trade.combinedEnd.day ? 1 : 0);
  } else {
    diff = Math.ceil(Math.abs(props.trade.combinedEnd.diff(props.trade.forecastEnd, ['months']).months)) -
      (props.trade.forecastEnd.day < props.trade.combinedEnd.day ? 1 : 0);
  }

  return diff;
});

const showTeleports = ref(false);

onMounted(() => {
  showTeleports.value = true;
});
</script>

<style scoped></style>
